import { mapValues } from 'lodash';
import {
  type AnyObject,
  ArraySchema,
  type ISchema,
  Lazy,
  ObjectSchema,
  type Schema,
  type SchemaDescription,
  type SchemaFieldDescription,
} from 'yup';

export interface IArrayValidationDescription {
  type: 'array';
  schemas: IValidationDescription[];
  desc: ArraySchema<any, any>;
  path: string;
  innerType: SchemaFieldDescription | undefined;
  value: any[];
}

export interface IObjectValidationDescription {
  type: 'object';
  schemas: Record<string, IValidationDescription>;
  desc: ObjectSchema<AnyObject>; // Al momento per gli oggetti e gli array non facciamo il `describe`
  path: string;
  value: Record<string, any> | null | undefined;
}

export interface IPrimitiveValidationDescription {
  type: 'primitive';
  desc: SchemaDescription;
  value: any;
  path: string;
}

/**
 * Rappresenta una descrizione di uno schema di validazione.
 */
export type IValidationDescription =
  | IArrayValidationDescription
  | IObjectValidationDescription
  | IPrimitiveValidationDescription;

type ResolveOptions = Parameters<Schema<any>['resolve']>[0];

const joinPath = (path: string | undefined, key: string) =>
  path ? `${path}.${key}` : key;

export function describeSchema(
  rawSchema: ISchema<any> | any,
  options: ResolveOptions & { path?: string },
): IValidationDescription {
  const schema = rawSchema.resolve(options);

  if (schema instanceof ArraySchema) {
    if (!schema.innerType || !Array.isArray(options.value)) {
      return {
        type: 'array',
        desc: schema,
        value: options.value,
        innerType: schema.innerType?.describe(options),
        path: options.path ?? '',
        schemas: [
          // Ritorno una schema fittizio in caso non ci siano elementi
          describeSchema(schema.innerType, {
            ...options,
            path: joinPath(options.path, '0'),
            value: options.value?.[0],
          }),
        ] as IValidationDescription[],
      };
    }

    // let nextOptions = { ...options, parent: options.value };
    return {
      type: 'array',
      desc: schema,
      value: options.value,
      path: options.path ?? '',
      innerType: schema.innerType.describe(options),
      schemas:
        options.value?.map((v: any, i) =>
          describeSchema(schema.innerType!, {
            ...options,
            path: joinPath(options.path, `${i}`),
            value: v,
          }),
        ) ?? [],
    } as IValidationDescription;
  }
  if (schema instanceof ObjectSchema) {
    const schemas = mapValues(schema.fields, (field, key) => {
      return describeSchema(field, {
        ...options,
        path: joinPath(options.path, key),
        value: options.value?.[key],
        parent: options.value,
        from: [{ schema, value: options.value }, ...(options.from ?? [])],
      });
    });

    return {
      type: 'object',
      desc: schema,
      value: options.value,
      path: options.path ?? '',
      schemas,
    };
  }
  if (schema instanceof Lazy) {
    // Nested lazy schema -> sono supportati
    const lazied = describeSchema(schema.resolve(options), options);
    return lazied;
  }
  return {
    type: 'primitive',
    value: options.value,
    path: options.path ?? '',
    desc: schema.describe(options),
  };
}
